import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Handler, Renderer} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {TooltipExplicitCondition} from '../../../common/tooltips/tooltipExplicitCondition';

/*
 * Props.
 */

export interface SetupGuideTaskProps {
  label: ReactNode;
  isComplete: boolean;
  hasHover?: boolean;
  renderDisabledTooltip?: Renderer;
  onClick: Handler;
}

/*
 * Style.
 */

interface StyledChromelessButtonProps {
  $isComplete: boolean;
}
const StyledChromelessButton = styled(ChromelessButton)<StyledChromelessButtonProps>`
  display: flex;
  flex-direction: row;
  align-items: center;

  box-sizing: border-box;
  height: 32px;
  border-radius: 100px;
  padding: 8px;

  color: ${(p) => p.theme.greys.shade80};
  ${(p) => addButtonStyles(Boolean(p.isDisabled), p.$isComplete, Boolean(p.hasHover))}
  ${(p) => maybeAddDisabledCursor(Boolean(p.isDisabled))}
`;

const StyledTaskCircleDiv = styled.div`
  border-radius: 50%;
  border: 1px solid ${(p) => p.theme.greys.shade50};
  width: 16px;
  height: 16px;
`;

const StyledLabelDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  margin-left: 8px;
`;

/*
 * Component.
 */

export const SetupGuideTask: FC<SetupGuideTaskProps> = ({
  label,
  isComplete,
  renderDisabledTooltip,
  hasHover,
  onClick,
}) => {
  const isDisabled = Boolean(renderDisabledTooltip);

  return (
    <TooltipCoordinator render={renderDisabledTooltip} placement="top">
      <TooltipExplicitCondition isEnabled={isDisabled} />
      <StyledChromelessButton
        isDisabled={isDisabled}
        $isComplete={isComplete}
        hasHover={hasHover}
        onClick={onClick}
      >
        {renderTaskCircle(isDisabled, isComplete)}
        <StyledLabelDiv>{label}</StyledLabelDiv>
      </StyledChromelessButton>
    </TooltipCoordinator>
  );
};

/*
 * Helpers.
 */

function renderTaskCircle(isDisabled: boolean, isComplete: boolean) {
  if (isDisabled && isComplete) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name="checkmarkCircleSmall" color={theme.greys.shade60} />}
      </UserThemeConsumer>
    );
  }
  if (isComplete) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name="checkmarkCircleSmall" color={theme.palette.green.shade40} />}
      </UserThemeConsumer>
    );
  }
  return <StyledTaskCircleDiv />;
}

function addButtonStyles(isDisabled: boolean, isComplete: boolean, hasHover: boolean) {
  if (isDisabled) {
    return css`
      background: ${(p) => p.theme.greys.shade40};
      border: 1px solid ${(p) => p.theme.alphas.black20};
      ${(p) => boxShadow(p.theme, `0px 1px 1px ${p.theme.alphas.black10}`)};
    `;
  }

  if (isComplete && hasHover) {
    return css`
      background: ${(p) => p.theme.palette.green.shade20};
      border: 1px solid ${(p) => p.theme.alphas.transparent};
      ${(p) => boxShadow(p.theme, `0px 1px 1px ${p.theme.alphas.transparent}`)};
    `;
  }

  if (isComplete) {
    return css`
      background: ${(p) => p.theme.palette.green.shade10};
      border: 1px solid ${(p) => p.theme.alphas.transparent};
      ${(p) => boxShadow(p.theme, `0px 1px 1px ${p.theme.alphas.transparent}`)};

      &:hover {
        background: ${(p) => p.theme.palette.green.shade20};
      }
    `;
  }

  if (hasHover) {
    return css`
      background: ${(p) => p.theme.greys.shade20};
      border: 1px solid ${(p) => p.theme.alphas.black20};
      ${(p) => boxShadow(p.theme, `0px 1px 1px ${p.theme.alphas.black10}`)};
    `;
  }

  return css`
    background: ${(p) => p.theme.greys.white};
    border: 1px solid ${(p) => p.theme.alphas.black20};
    ${(p) => boxShadow(p.theme, `0px 1px 1px ${p.theme.alphas.black10}`)};

    &:hover {
      background: ${(p) => p.theme.greys.shade20};
    }
  `;
}

function maybeAddDisabledCursor(isDisabled: boolean) {
  if (!isDisabled) {
    return undefined;
  }

  return css`
    cursor: not-allowed;

    & * {
      cursor: not-allowed;
    }
  `;
}
